import React, { useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import AnnouncementBar from '../components/AnnouncementBar';
import Footer from '../components/Footer';
import SponsorsSection from '../components/SponsorsSection';
import ScrollToTop from '../components/ScrollToTop';
import TornPaperWrapper from '../components/TornPaperWrapper';
import { getCmsImagePath } from '../cms/cmsData';
import { useCms } from '../cms/CmsContext';
import { buildLineupDaySections, formatArtistNames, getArtistLineup, getLineupArtists } from '../data/artistLineup';
import { isCurrentUserStrictAdmin } from '../utils/adminCheck';
import './TicketsFunnel.css';
import './Tickets.css';
import './MusicEntertainment.css';
import { getArabicTranslation } from '../utils/arabicTranslations';

const PerformerGrid = ({ performers, ticketPath, compact }) => (
  <div className={`music-ent-grid ${compact ? 'music-ent-grid-2' : ''}`}>
    {performers.map((performer, index) => (
      <Link
        key={`${performer.name}-${index}`}
        to={ticketPath}
        className="music-ent-card-link"
        title={performer.name}
      >
        <img
          loading="lazy"
          src={performer.image}
          alt={performer.name}
          className="music-ent-card-image"
        />
      </Link>
    ))}
  </div>
);

const AmphitheaterTicketsFunnel = () => {
  const { content } = useCms();
  const artistLineup = getArtistLineup(content);
  const daySections = buildLineupDaySections(artistLineup).filter((day) => day.key !== 'friday');
  const saturdayNames = formatArtistNames(getLineupArtists(artistLineup, { day: 'saturday', stage: 'pac-amp', role: 'artist' }));
  const sundayNames = formatArtistNames(getLineupArtists(artistLineup, { day: 'sunday', stage: 'pac-amp', role: 'artist' }));
  const isStrictAdmin = useMemo(() => isCurrentUserStrictAdmin(), []);

  useEffect(() => {
    document.title = 'OC MENA Festival | Amphitheater Tickets';
  }, []);

  return (
    <div className="tickets-funnel-wrapper">
      <AnnouncementBar />

      <section className="tickets-funnel-hero">
        <div className="hero-background-wrapper">
          <video src={"/hero-final.mp4"} alt="oc mena festival" className="hero-background-video" autoPlay muted loop playsInline />
          <div className="hero-gradient-overlay"></div>
        </div>

        <TornPaperWrapper>
          <h1 className="funnel-heading">Choose Your Pacific Amphitheatre Night</h1>
          <p className="amp-funnel-subheading arabic-text on-dark arabic-below">{getArabicTranslation('Choose Your Pacific Amphitheatre Night')}</p>
          <p className="amp-funnel-subheading">
            Choose your concert date to see the dedicated seating map and buy tickets for that show.
          </p>

          <div className="amp-funnel-grid">
            <div className="funnel-card amp-funnel-card">
              <h3 className="amp-funnel-day-heading">Saturday, June 20 <span className="arabic-text on-dark">{getArabicTranslation('Saturday')}</span></h3>
              <h2 className="amp-card-title">{saturdayNames || 'Lineup coming soon'}</h2>

              <div className="amp-funnel-card-image amp-funnel-card-image-full">
                <img loading="lazy" src="/Artist Collage Funnel Page -- Saturday - Full (1).webp" alt={saturdayNames || 'Saturday PAC AMP lineup'} />
              </div>

              <div className="amp-funnel-date">Pacific Amphitheatre · 5 PM</div>

              <p className="funnel-card-description">
                Browse the Saturday seating map for {saturdayNames || 'the PAC AMP lineup'} and reserve your seats for June 20, 2026
              </p>

              <div className="funnel-notice amp-funnel-notice">
                <span className="funnel-notice-icon">i</span>
                <span className="funnel-notice-text">
                  Your amphitheater ticket <strong>includes festival admission for Saturday, June 20.</strong>
                </span>
              </div>

              {isStrictAdmin ? (
                <Link to="/amphitheater-tickets-saturday" className="funnel-cta-btn">
                  Buy Saturday PAC AMP Tickets
                </Link>
              ) : (
                <span className="funnel-cta-btn" style={{ background: '#E1E7EF', borderColor: '#E1E7EF', color: '#6b7280', cursor: 'not-allowed', pointerEvents: 'none' }}>
                  SOLD OUT
                </span>
              )}
            </div>

            <div className="funnel-card amp-funnel-card">
              <h3 className="amp-funnel-day-heading">Sunday, June 21 <span className="arabic-text on-dark">{getArabicTranslation('Sunday')}</span></h3>
              <h2 className="amp-card-title">{sundayNames || 'Lineup coming soon'}</h2>

              <div className="amp-funnel-card-image amp-funnel-card-image-full">
                <img loading="lazy" src="/Artist Collage Funnel Page -- Sunday.webp" alt={sundayNames || 'Sunday PAC AMP lineup'} />
              </div>

              <div className="amp-funnel-date">Pacific Amphitheatre · 5 PM</div>

              <p className="funnel-card-description">
                Browse the Sunday seating map for {sundayNames || 'the PAC AMP lineup'} and reserve your seats for June 21, 2026
              </p>

              <div className="funnel-notice amp-funnel-notice">
                <span className="funnel-notice-icon">i</span>
                <span className="funnel-notice-text">
                  Your amphitheater ticket <strong>includes festival admission for Sunday, June 21.</strong>
                </span>
              </div>

              {isStrictAdmin ? (
                <Link to="/amphitheater-tickets-sunday" className="funnel-cta-btn">
                  Buy Sunday PAC AMP Tickets
                </Link>
              ) : (
                <span className="funnel-cta-btn" style={{ background: '#E1E7EF', borderColor: '#E1E7EF', color: '#6b7280', cursor: 'not-allowed', pointerEvents: 'none' }}>
                  SOLD OUT
                </span>
              )}
            </div>
          </div>

          <div className="parking-notice" style={{ marginTop: '1.5rem', marginBottom: '2rem' }}>
            <p>*You are welcome to come early and enjoy the festival. Festival opens at 11 AM and the Amphitheatre concert gates open at 5 PM</p>
            <p>*Parking is $15, paid directly to the OC Fair & Events Center on site. Carpooling is encouraged.</p>
          </div>

          <section className="music-ent-container music-ent-compact">
            {daySections.map((day) => (
              <article key={day.key} className="music-ent-day-section">
                <header className="music-ent-day-header">
                  <h2>
                    {day.dayLabel}
                    <span className={`arabic-text on-dark arabic-inline ${day.key === 'sunday' ? 'sunday-arabic' : ''}`}>{getArabicTranslation(day.dayLabel)}</span>
                  </h2>
                  <p>{day.dateLabel}</p>
                </header>

                <div className="music-ent-stage-block music-ent-pac-block">
                  {day.pacAmp.hasPerformances && (
                    <h3 className="music-ent-stage-title">
                      Pacific Amphitheatre (PAC AMP Stage)
                      <span className="arabic-text on-white">{getArabicTranslation('Pacific Amphitheatre')}</span>
                    </h3>
                  )}
                  {day.pacAmp.hasPerformances ? (
                    <>
                      <p className="music-ent-stage-note">
                        {day.pacAmp.notePrefix}{' '}
                        <strong style={{ color: isStrictAdmin ? '#0f766e' : '#dc2626' }}>
                          {isStrictAdmin ? 'ADMIN TEST PURCHASE ENABLED' : 'SOLD OUT'}
                        </strong>
                      </p>
                      <PerformerGrid performers={day.pacAmp.performers} ticketPath={day.pacAmp.ticketPath} />
                      {day.pacAmp.djName && <div className="music-ent-dj-row">
                        <span className="music-ent-dj-label">DJ Appearance by</span>
                        {day.pacAmp.djImage && <img
                          src={day.pacAmp.djImage}
                          alt={`DJ ${day.pacAmp.djName}`}
                          className="music-ent-dj-avatar"
                        />}
                        <span className="music-ent-dj-name">{day.pacAmp.djName}</span>
                      </div>}
                      <div className="music-ent-stage-btn-wrap">
                        {isStrictAdmin ? (
                          <Link to={day.pacAmp.ticketPath} className="music-ent-stage-btn">
                            {day.pacAmp.buttonText}
                          </Link>
                        ) : (
                          <span className="music-ent-stage-btn" style={{ background: '#E1E7EF', borderColor: '#E1E7EF', color: '#6b7280', cursor: 'not-allowed', pointerEvents: 'none' }}>
                            SOLD OUT
                          </span>
                        )}
                      </div>
                    </>
                  ) : (
                    <div className="music-ent-no-pac">{day.pacAmp.noPerformanceText}</div>
                  )}
                </div>
              </article>
            ))}
          </section>

          <div className="funnel-support-images" style={{ marginTop: '1rem' }}>
            <div className="funnel-support-image-frame">
              <img
                loading="lazy"
                src="/ocmena-official-flyer(1080x1350)-c.png"
                alt="OC MENA Festival Official Flyer"
                className="funnel-support-image"
              />
            </div>
            <div className="funnel-support-image-frame">
              <img loading="lazy" src="/how_tickets_work.jpeg" alt="How tickets work" className="funnel-support-image" />
            </div>
            <div className="funnel-support-image-frame">
              <img
                loading="lazy"
                src="/map.png"
                alt="OC MENA Festival Event Map"
                className="funnel-support-image"
              />
            </div>
          </div>
        </TornPaperWrapper>

        <div className="lanterns-container">
          <img loading="lazy" src={getCmsImagePath('lanterns', '/lanterns.webp')} alt="festival lanterns" className="lanterns-image" />
        </div>
      </section>

      <SponsorsSection />
      <Footer />
      <ScrollToTop />
    </div>
  );
};

export default AmphitheaterTicketsFunnel;
